import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import setActiveTopMenuDropdownOperation from 'editor/src/store/app/operation/setActiveTopMenuDropdownOperation';
import applyFontStylesToSelectionOperation from 'editor/src/store/fonts/operation/applyFontStylesToSelectionOperation';
import getCurrentFontStylesProperty from 'editor/src/store/fonts/selector/getCurrentFontStylesProperty';
import { getSharedFontSize } from 'editor/src/store/fonts/selector/getSharedFontStyles';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import DropDown from 'editor/src/component/DropDown';
import IconMinus from 'editor/src/component/Icon/IconMinus';
import IconPlus from 'editor/src/component/Icon/IconPlus';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';
import WithTooltip from 'editor/src/component/WithTooltip';

import FontSizeButton from './FontSizeButton';
import getFontSizes from './getFontSizes';

import styles from './index.module.scss';

const getFontSizeDisplayValue = (fontSizes: number[]): string => {
  if (!fontSizes.length) {
    return '--';
  }
  const roundedSizes = fontSizes.map((size) => Math.round(size));
  const areAllEqual = roundedSizes.every((size) => size === roundedSizes[0]);
  return areAllEqual ? roundedSizes[0].toString() : '--';
};

export const canShow: CanShow = (state, { isPersonalizationLocked }) => !isPersonalizationLocked;

function ButtonFontSize({ mode }: MenuItemProps) {
  const minFontSize = useSelector((state) => getHostSetting(state, 'minFontSize'));
  const maxFontSize = useSelector((state) => getHostSetting(state, 'maxFontSize'));
  const fontSizes = useSelector(getSharedFontSize);
  const currentWrapping = useSelector((state) => getCurrentFontStylesProperty(state, 'wrapping'));
  const dispatch = useDispatch();
  const [editSize, setEditSize] = useState(false);
  const [dropDownVisible, setDropDownVisible] = useState(false);
  const [fontInputValue, setFontInputValue] = useState(() => getFontSizeDisplayValue(fontSizes));
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const availableFontSizes = useMemo(() => getFontSizes(minFontSize, maxFontSize), [minFontSize, maxFontSize]);

  useEffect(() => {
    setFontInputValue(getFontSizeDisplayValue(fontSizes));
  }, [fontSizes]);

  const handleWindowEvent = (e: MouseEvent) => {
    if (
      e.target &&
      inputWrapRef?.current &&
      e.target instanceof Node &&
      !inputWrapRef.current.contains(e.target) &&
      editSize
    ) {
      setEditSize(false);
      setFontInputValue(getFontSizeDisplayValue(fontSizes));
    }
  };

  useEffect(() => {
    if (!editSize) {
      return undefined;
    }

    inputRef.current?.focus();
    inputRef.current?.select();

    const handle = window.setTimeout(() => window.addEventListener('click', handleWindowEvent), 0);
    return () => {
      window.clearTimeout(handle);
      window.removeEventListener('click', handleWindowEvent);
    };
  }, [editSize]);

  const isWrappingFit = currentWrapping === 'fit';

  const canIncreaseFontSize = useCallback(() => {
    if (!fontSizes.length || !maxFontSize || isWrappingFit) {
      return false;
    }
    return fontSizes.some((size) => Math.round(size) < maxFontSize);
  }, [fontSizes, maxFontSize, isWrappingFit]);

  const canDecreaseFontSize = useCallback(() => {
    if (!fontSizes.length || !minFontSize || isWrappingFit) {
      return false;
    }
    return fontSizes.some((size) => Math.round(size) > minFontSize);
  }, [fontSizes, minFontSize, isWrappingFit]);

  const setFontSize = useCallback(
    (fontSize: number) => {
      dispatch(setActiveTopMenuDropdownOperation(undefined));
      dispatch(applyFontStylesToSelectionOperation({ fontSize }));
    },
    [dispatch],
  );

  const handlePlusButtonClick = () => {
    if (canIncreaseFontSize() && fontSizes) {
      dispatch(
        applyFontStylesToSelectionOperation({
          fontSize: (currentSize) => Math.min(Math.round(currentSize) + 1, maxFontSize),
        }),
      );
    }
  };

  const handleMinusButtonClick = () => {
    if (canDecreaseFontSize() && fontSizes) {
      dispatch(
        applyFontStylesToSelectionOperation({
          fontSize: (currentSize) => Math.max(Math.round(currentSize) - 1, minFontSize),
        }),
      );
    }
  };

  const handleClickOutsideDropdown = useCallback(() => {
    setDropDownVisible(false);
    setEditSize(false);
  }, []);

  const handleValueButtonClick = () => {
    setEditSize(true);
    if (mode !== 'label-icon') {
      setDropDownVisible(true);
    }
  };

  const handleFontSizeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFontInputValue(value);
  };

  const isValueCorrect = (fontSizeText: string) => {
    const fontSize = parseInt(fontSizeText, 10);
    return fontSize <= maxFontSize && fontSize >= minFontSize && !Number.isNaN(fontSize);
  };

  const filterInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      applyFontSize();
      return;
    }

    if (
      e.key === '.' ||
      e.key === ',' ||
      e.key === 'e' ||
      (fontSizes && fontSizes.length > 0 && Math.min(...fontSizes) <= minFontSize && e.key === 'ArrowDown') ||
      (fontSizes && fontSizes.length > 0 && Math.max(...fontSizes) >= maxFontSize && e.key === 'ArrowUp')
    ) {
      e.preventDefault();
    }
  };

  const applyFontSize = () => {
    if (isValueCorrect(fontInputValue) && fontInputValue !== fontSizes.toString()) {
      const parsedInputValue = parseInt(fontInputValue, 10);
      if (fontSizes && parsedInputValue >= minFontSize && parsedInputValue <= maxFontSize) {
        setFontSize(parsedInputValue);
      }
    }
  };

  return (
    <div className={cn(styles.ButtonFontSize, 'cy-font-size')}>
      <button
        className={cn(
          styles.minusButton,
          styles.button,
          { [styles.disabled]: !canDecreaseFontSize() },
          'cy-button-font-size-minus',
        )}
        onClick={handleMinusButtonClick}
        aria-label="Decrease font size"
      >
        <IconMinus />
      </button>
      {editSize && !isWrappingFit ? (
        <div ref={inputWrapRef} className={styles.inputWrap}>
          <WithTooltip
            overlay={t('editor-font-size-must-be-between', {
              min: minFontSize,
              max: maxFontSize,
            })}
            visible={!isValueCorrect(fontInputValue)}
            placement="bottom"
          >
            <input
              ref={inputRef}
              className={cn(
                styles.input,
                { [styles.wrongValue]: !isValueCorrect(fontInputValue) },
                'cy-font-size-input',
              )}
              type="number"
              value={fontInputValue}
              onChange={handleFontSizeInputChange}
              onKeyDown={filterInput}
              onClick={handleValueButtonClick}
              onBlur={applyFontSize}
            />
          </WithTooltip>
          {dropDownVisible && (
            <DropDown
              isVisible
              wrapperClassName={styles.dropDownWrapper}
              className={styles.dropDown}
              openPosition="bottom-center"
              onClickOutside={handleClickOutsideDropdown}
              maxHeight={250}
            >
              {availableFontSizes.map((value) => (
                <FontSizeButton
                  key={value}
                  value={value}
                  isActive={fontSizes && fontSizes.length > 0 && fontSizes.every((size) => Math.round(size) === value)}
                  onClick={handleClickOutsideDropdown}
                />
              ))}
            </DropDown>
          )}
        </div>
      ) : (
        <button
          className={cn(styles.valueButton, styles.button, { [styles.disabled]: isWrappingFit }, 'cy-button-font-size')}
          onClick={handleValueButtonClick}
        >
          {getFontSizeDisplayValue(fontSizes)}
        </button>
      )}
      <button
        className={cn(
          styles.plusButton,
          styles.button,
          { [styles.disabled]: !canIncreaseFontSize() },
          'cy-button-font-size-plus',
        )}
        onClick={handlePlusButtonClick}
        aria-label="Increase font size"
      >
        <IconPlus />
      </button>
    </div>
  );
}

export default React.memo(ButtonFontSize);
